import { Box, Button, Modal, Typography } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import { useDayCoach } from "./DayCoachContext";
import { goalEffortLine } from "../../pages/CRM/dayPlan";

const INK = "#0f172a";

export default function DayGuide() {
  const coach = useDayCoach();
  if (!coach?.enabled) return null;

  const current = coach.active[0] || null;
  const finished = !coach.loading && coach.remaining.length === 0;
  const onlySkipped = !current && coach.skipped.length > 0;

  return (
      <Modal open={coach.guideOpen} onClose={coach.closeGuide}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "calc(100% - 28px)", sm: 520 },
            outline: "none",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 520,
              bgcolor: "#fff",
              borderRadius: "24px",
              overflow: "hidden",
              boxShadow: "0 30px 80px rgba(15, 23, 42, 0.22)",
            }}
          >
            <Box sx={{ height: 4, bgcolor: "#0f172a" }} />

            {finished ? (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    mx: "auto",
                    mb: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#ecfdf5",
                    color: "#059669",
                  }}
                >
                  <CheckRounded sx={{ fontSize: 32 }} />
                </Box>
                <Typography sx={{ fontSize: "1.45rem", fontWeight: 750, color: INK, letterSpacing: "-0.03em" }}>
                  Día listo
                </Typography>
                <Typography sx={{ fontSize: "0.92rem", color: "#64748b", mt: 1, lineHeight: 1.5 }}>
                  Terminaste la lista. Ya puedes cerrar el día.
                </Typography>
                <Button
                  onClick={coach.closeGuide}
                  variant="contained"
                  sx={{ mt: 3, textTransform: "none", fontWeight: 750, borderRadius: "12px", bgcolor: INK, boxShadow: "none", px: 3, "&:hover": { bgcolor: "#1e293b", boxShadow: "none" } }}
                >
                  Cerrar el día
                </Button>
              </Box>
            ) : onlySkipped ? (
              <Box sx={{ px: 3, py: 3.5 }}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>
                  En pausa
                </Typography>
                <Typography sx={{ fontSize: "1.35rem", fontWeight: 750, color: INK, letterSpacing: "-0.03em", mt: 0.75, lineHeight: 1.2 }}>
                  Quedan {coach.skipped.length} en pausa
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: "#64748b", mt: 1, lineHeight: 1.5 }}>
                  {coach.goal ? goalEffortLine(coach.goal.done, coach.goal.target) : "Retómalos solo si todavía te falta la meta."}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 2.5 }}>
                  <Button onClick={coach.closeGuide} sx={{ textTransform: "none", fontWeight: 650, color: "#64748b", borderRadius: "12px" }}>
                    Luego
                  </Button>
                  <Button
                    variant="contained"
                    onClick={coach.resumeSkipped}
                    sx={{ flex: 1, textTransform: "none", fontWeight: 750, borderRadius: "12px", bgcolor: INK, boxShadow: "none", "&:hover": { bgcolor: "#1e293b", boxShadow: "none" } }}
                  >
                    Retomarlos
                  </Button>
                </Box>
              </Box>
            ) : current ? (
              <Box sx={{ px: 3, pt: 2.25, pb: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.75 }}>
                  <Button onClick={coach.closeGuide} sx={{ textTransform: "none", fontWeight: 650, color: "#94a3b8", minWidth: 0, p: 0 }}>
                    Cerrar
                  </Button>
                </Box>

                <Typography sx={{ fontSize: "0.72rem", fontWeight: 750, letterSpacing: "0.06em", textTransform: "uppercase", color: current.bucket === "overdue" || current.bucket === "urgent" ? "#be123c" : "#94a3b8" }}>
                  {current.verb}
                </Typography>

                <Typography sx={{ fontSize: "1.55rem", fontWeight: 760, color: INK, letterSpacing: "-0.035em", lineHeight: 1.1, mt: 0.6 }}>
                  {current.lead.companyName}
                </Typography>
                {current.lead.contactName && (
                  <Typography sx={{ fontSize: "0.88rem", color: "#64748b", mt: 0.4 }}>{current.lead.contactName}</Typography>
                )}

                {(() => {
                  const rows = [
                    current.phone && { label: "Teléfono", value: current.phone },
                    current.whatsapp && current.whatsapp !== current.phone && { label: "WhatsApp", value: current.whatsapp },
                    current.email && { label: "Correo", value: current.email },
                  ].filter(Boolean);
                  if (!rows.length) return null;
                  return (
                    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {rows.map((item) => (
                        <Box key={item.label}>
                          {rows.length > 1 && (
                            <Typography sx={{ fontSize: "0.68rem", fontWeight: 750, letterSpacing: "0.05em", textTransform: "uppercase", color: "#94a3b8" }}>
                              {item.label}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: rows.length > 1 ? "1.15rem" : "1.65rem", fontWeight: 750, letterSpacing: "-0.03em", color: INK, lineHeight: 1.2, wordBreak: "break-word" }}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
                <Typography sx={{ mt: 1.25, fontSize: "0.9rem", color: "#475569", lineHeight: 1.45 }}>
                  {current.detail}
                </Typography>

                {coach.toast && (
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#059669", mt: 1.25 }}>
                    {coach.toast}
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1, mt: 2.5 }}>
                  <Button
                    onClick={() => coach.skipCurrent(current.id)}
                    sx={{ textTransform: "none", fontWeight: 650, color: "#64748b", borderRadius: "12px" }}
                  >
                    Saltar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => coach.openCall(current.lead)}
                    sx={{ flex: 1, textTransform: "none", fontWeight: 750, borderRadius: "12px", bgcolor: INK, boxShadow: "none", py: 1.15, "&:hover": { bgcolor: "#1e293b", boxShadow: "none" } }}
                  >
                    Empezar llamada
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ px: 3, py: 4 }}>
                <Typography sx={{ color: "#64748b" }}>Cargando tu siguiente lead…</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
  );
}
